import tune from "@icons/tune.svg";
import rabbitRunning from "@icons/rabbit_running.svg";
import rabbit from "@icons/rabbit.svg";
import schedule from "@icons/schedule.svg";
import check from "@icons/check.svg";
import warning from "@icons/warning.svg";
import close from "@icons/close.svg";

import Request from "./Request";
import { Hatch, SafeString } from "./Hatch";
import { FetchFormModal } from "../components/Modal";
import { ToggleableElement } from "../global/interfaces";

interface cc {
  code: string;
  name: string;
}

/*
    Simplified delay mechanism as a promise
   */
export const wait = (time = 0) => {
  return new Promise((resolve) => setTimeout(() => resolve(null), time));
};

interface toast {
  id?: string;
  type?: string;
  message: SafeString | string;
  title?: SafeString | string;
  action?: {
    type: string;
    label: string;
    href: string;
  };
}

// core methods and utilities should be defined as a method on this class.
export default class Utils {
  /*
      converts a size in bytes to a 'human' format
     */
  static humanByteSize = (bytes: number, si: number) => {
    const thresh = si ? 1000 : 1024;
    if (Math.abs(bytes) < thresh) {
      return {
        string: bytes + " B",
      };
    }
    let units = si
      ? ["kB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"]
      : ["KiB", "MiB", "GiB", "TiB", "PiB", "EiB", "ZiB", "YiB"];
    let u = -1;
    do {
      bytes /= thresh;
      ++u;
    } while (Math.abs(bytes) >= thresh && u < units.length - 1);
    return {
      value: parseFloat(bytes.toFixed(1)),
      units: units[u],
      string: `${bytes.toFixed(1)}${units[u]}`,
    };
  };

  /*
      Copies input content to the OS clipboard
     */
  static copyToClipboard = (el: Element) => {
    navigator.clipboard.writeText(el.getAttribute("data-clipboard")).then(() => {
      el.setAttribute("data-label-cached", el.innerHTML);
      el.innerHTML = `
        <div class="ma-icon small">${check}
        ${!el.querySelector(".ma-icon") ? "<span>Copied</span>" : ""}
        </div>`;
      if (el.classList.contains("primary")) {
        el.classList.remove("primary");
        el.classList.add("secondary");
      } else {
        el.classList.add("primary");
        el.classList.remove("secondary");
      }
      setTimeout(() => {
        el.innerHTML = el.getAttribute("data-label-cached");
        if (el.classList.contains("secondary")) {
          el.classList.add("primary");
          el.classList.remove("secondary");
        } else {
          el.classList.remove("primary");
          el.classList.add("secondary");
        }
      }, 500);
    });
  };

  /*
      Show all items with a hidden class in container
    */
  static toggleShowMore = (el: ToggleableElement) => {
    if (!el.toggled) {
      el.toggled = true;
      el.innerText = "Show less";
      el.parentElement.parentElement.querySelectorAll(".hidden:not(textarea)").forEach((item) => {
        item.classList.add("toToggle");
        item.classList.remove("hidden");
      });
    } else {
      el.toggled = false;
      el.innerText = "Show more";
      el.parentElement.parentElement.querySelectorAll(".toToggle").forEach((item) => {
        item.classList.remove("toToggle");
        item.classList.add("hidden");
      });
    }
  };

  static defang = (url: string): string => {
    url = url.replace("https://", "hxxps://");
    url = url.replace("http://", "hxxp://");
    url = url.replace("ftp://", "fxp://");
    url = url.replaceAll(".", "[.]");
    return url;
  };

  static refang = (url: string): string => {
    url = url.replace("hxxps://", "https://");
    url = url.replace("hxxp://", "http://");
    url = url.replace("fxp://", "ftp://");
    url = url.replaceAll("[.]", ".");
    return url;
  };

  /*
      Checks if element is overflow and direction
      {
        isOverflow: true | false
        dir: "both" | "left" | "right"
      }
      */
  static getOverflow = (el: Element) => {
    let direction: string;
    if (el.scrollLeft == 0) {
      direction = "left";
    } else if (Math.round(el.scrollWidth) - 1 <= Math.round(el.getBoundingClientRect().width + el.scrollLeft)) {
      direction = "right";
    } else if (el.scrollWidth > el.clientWidth) {
      direction = "both";
    } else {
      direction = null;
    }

    let overflow = {
      isOverflow: el.scrollWidth > el.clientWidth,
      dir: direction,
    };
    return overflow;
  };

  static scrollAndBlink = async (el: HTMLElement) => {
    el.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });

    for (let i = 0; i < 12; i++) {
      await wait(300);
      el.classList.toggle("blink");
    }
  };

  /*
      Returns the color label for scores
    */
  static getSeverity = (score: number) => {
    let statusColor = "normal";

    if (score > 4) {
      statusColor = "warning";
    }
    if (score > 7) {
      statusColor = "alert";
    }

    return statusColor;
  };

  /*
      Simple form validation
    */

  static validateForm = (form: HTMLFormElement) => {
    let requireds = form.querySelectorAll("[required]");
    let valid = true;
    requireds.forEach((item: HTMLInputElement) => {
      if (!item.value) {
        valid = false;
      }
    });
    if (valid) {
      return true;
    } else {
      globalThis.notification.notify({
        title: "Failed to send",
        message: "Please fill out the required field(s).",
        type: "danger",
        dismiss: "Dismiss",
        timer: 5000,
      });
      return false;
    }
  };

  /*
      Returns a new Request class
      - for documentation, see file ./classes/Request.es6
      - usage: Utilities.Request({...}) => Promise
     */
  static Request = (props: any) => {
    return new Request(props);
  };

  static Hash = (str = "") => {
    let hash = 0;
    if (str.length == 0) return hash;
    for (let i = 0; i < str.length; i++) {
      let char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return hash;
  };

  static stateToIcon = (state: string) => {
    let iconTemplate = (str: string) => {
      return `<div class="ma-icon default">${str}</div>`;
    };
    let statusMap = {
      static_analysis: {
        icon: iconTemplate(tune),
        label: "Scheduler",
      },
      running: {
        icon: iconTemplate(rabbitRunning),
        label: "Running",
      },
      reported: {
        icon: iconTemplate(check),
        label: "Reported",
      },
      scheduled: {
        icon: iconTemplate(rabbit),
        label: "Scheduled",
      },
      pending: {
        icon: iconTemplate(schedule),
        label: "Pending",
      },
      failed: {
        icon: iconTemplate(warning),
        label: "Failed",
      },
    };

    return statusMap[state];
  };

  static spawnToast = (toast: toast) => {
    let holder = document.querySelector("#toasts");
    let el = document.createElement("div");
    if (toast.id) el.setAttribute("ID", toast.id);
    el.classList.add("toast");
    el.classList.add(toast.type ? toast.type : "info");
    el.innerHTML = Hatch`
            <h3>${toast.title}</h3>
            <p>${toast.message}</p>
            ${
              toast.action
                ? Hatch`
              <div class=button-bar>
                <a href="${toast.action.href}" class="button ${toast.action.type} small">
                  ${toast.action.label}
                </a>
              </div>
            `
                : ""
            }
            <button data-close-toast>
                <div class="ma-icon default">
                    ${new SafeString(close.toString())}
                </div>
            </button>`.toString();
    let closeEl = el.querySelector("[data-close-toast]");
    closeEl?.addEventListener("click", (e: Event) => {
      e.preventDefault();
      this.closeToast(el);
    });
    holder?.appendChild(el);
  };

  static formatTags = (tags: string[]) => {
    if (!tags || !tags.length) {
      return "";
    }
    let formatted = "";
    tags.forEach((tag) => {
      let prefix = tag.split(":")[0];
      let content = tag;
      let rhs = tag.split(":")[1];
      if (rhs) {
        content = rhs;
      }
      let tagClass = (() => {
        switch (prefix) {
          case "botnet":
            return "blue";
          case "campaign":
            return "purple";
          case "family":
            return "rose";
          case "brand":
            return "turquoise";
          default:
            return "";
        }
      })();

      formatted += Hatch`<a href="/s?q=${prefix}:${content}" title="${prefix}" class="${tagClass}">${content}</a>`;
    });
    return formatted;
  };

  static ccToLabel = async () => {
    let response = await fetch(location.origin + "/static/json/country-codes.json");
    return (await response.json()) as { code: string; name: string }[];
  };

  static closeToast = (toast: HTMLElement) => {
    toast.classList.add("out");
    toast.style.marginTop = `-${toast.getBoundingClientRect().height}px`;
    toast.style.opacity = "0";
    setTimeout(() => {
      toast?.remove();
    }, 400);
  };

  static feedback = () => {
    let feedbackform = Hatch`
            <h2>Give us feedback</h2>
            <p>Please let us know if you have any feedback for us. For example, if you are missing support for a specific malware family, you believe the scoring to be inaccurate, or if there is something you like/dislike in general.</p>
            <fieldset>
                <textarea rows="4" type="text" name="text" data-required="true" required></textarea>
                <label>Message*</label>
            </fieldset>
            <fieldset>
              <select name="category">
                <option value="bugs_and_issues">Bugs and Issues</option>
                <option value="general_feedback">General Feedback</option>
              </select>
              <label>Category</label>
            </fieldset>
            <fieldset>
              <input type="text" name="sample_id" value="${globalThis?.window?.page_data?.sample?.id || ""}">
              <label>Sample ID</label>
            </fieldset>
            <input type="hidden" name="url" value="${window.location}">
            <input type="hidden" name="trace_id" value="${window.trace_id}">
            <input type="hidden" name="user_agent" value="${window.navigator.userAgent}">
            <div class="button-bar">
                <button class="button primary mid" data-submit>Send Feedback</button>
            </div>
        `;

    new FetchFormModal({
      body: feedbackform,
      action: "/feedback",
      successMsg: "Thank you, your feedback has been successfully submitted.",
    });
  };
}

export const debounce = (delay: number, fun: (...args: any[]) => any) => {
  let timeout: number;
  return (...args: any[]) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      fun(...args);
    }, delay);
  };
};

export const deepEqual = (lhs: Record<string, any>, rhs: Record<string, any>) => {
  if (lhs === rhs) {
    return true;
  }
  if (!lhs) {
    return false;
  }
  const entries = Object.entries(lhs);

  if (Object.keys(rhs).length !== entries.length) {
    return false;
  }
  for (let [key, value] of entries) {
    const rh = rhs[key];
    if (rh === undefined) {
      return false;
    }
    if (typeof rh === "object") {
      if (!deepEqual(value, rh)) {
        return false;
      }
      continue;
    }
    if (value !== rh) {
      return false;
    }
  }
  return true;
};

export const matchImportUrl = (url: string) => {
  url = url.replace("https://tria.ge/", "");
  url = url.split("/")[0];

  let importReg = /^(19|2[0-9])(0[1-9]|1[0-2])(0[1-9]|[12][0-9]|3[01])-[abcdefghjklmnpqrstvwxyz123456789]{10}$/gm;
  return importReg.test(url);
};

export const isValidHttpUrl = (str: string) => {
  let url: URL;
  try {
    url = new URL(str);
  } catch (_) {
    return false;
  }

  return url.protocol === "http:" || url.protocol === "https:";
};

export const getChildIndex = (node: HTMLElement) => {
  return Array.prototype.indexOf.call(node.parentNode.children, node);
};

export let testSamlEmail = async (email: string) => {
  if (!globalThis.permissions.includes("login_saml")) {
    return false;
  }
  const response = await fetch("/api/login/saml/org", {
    headers: { "X-CSRF-Token": globalThis.csrf_token },
    body: JSON.stringify({ email: email }),
    method: "POST",
    credentials: "include",
  }).catch((err) => {
    console.error(err);
    return err;
  });
  if (!response.ok) {
    return null;
  }
  const res = await response.json();
  return await res.redirect;
};
